import React, { useState, useMemo } from "react";
import { Box, Collapse, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import SearchInput from "components/SearchInput";
import DeviceItem from "./DeviceItem";
import FavoriteItem from "./FavoriteItem";
import NvrItem from "./NvrItem";

/**
 * CameraSearchPanel
 * Encapsulates the Search input and the Favorite / Device / NVR sections.
 *
 * Props:
 *   devices          - array of { id, name, favorited, linkStatus }
 *   nvrs             - array of { id, name, cameras: [{ id, name, favorited? }] }
 *   onDeviceSettings - called with camera/device when settings icon clicked
 *   onPlayCamera     - called with camera/device when the row is clicked (play)
 *   onPlayCameras    - called with camera[] when section ▶ is clicked
 *   onToggleFavorite - called with deviceId when star is clicked
 */
const CameraSearchPanel = ({
  devices = [],
  nvrs = [],
  onDeviceSettings,
  onPlayCamera,
  onPlayCameras,
  onToggleFavorite,
  showSectionPlay = true,
}) => {
  const theme = useTheme();

  // ── search ──────────────────────────────────────────────────────────────────
  const [searchKeyword, setSearchKeyword] = useState("");
  const kw = searchKeyword.toLowerCase();

  // ── collapse states ──────────────────────────────────────────────────────────
  const [favoritesExpanded, setFavoritesExpanded] = useState(true);
  const [devicesExpanded, setDevicesExpanded] = useState(true);
  const [nvrExpanded, setNvrExpanded] = useState(true);

  // ── filtered data ────────────────────────────────────────────────────────────
  const filteredDevices = devices.filter((d) =>
    d.name?.toLowerCase().includes(kw)
  );

  const allNvrCameras = useMemo(
    () => nvrs.flatMap((nvr) => nvr.cameras || []),
    [nvrs]
  );

  const favoriteDevices = [...devices, ...allNvrCameras].filter(
    (d) => d.favorited === true && d.name?.toLowerCase().includes(kw)
  );

  const filteredNvrs = searchKeyword
    ? nvrs
        .filter(
          (nvr) =>
            nvr.name?.toLowerCase().includes(kw) ||
            (nvr.cameras || []).some((c) => c.name?.toLowerCase().includes(kw))
        )
        .map((nvr) => {
          const nvrMatches = nvr.name?.toLowerCase().includes(kw);
          return {
            ...nvr,
            cameras: nvrMatches
              ? nvr.cameras
              : (nvr.cameras || []).filter((c) => c.name?.toLowerCase().includes(kw)),
          };
        })
    : nvrs;

  const nvrCamerasInOrder = useMemo(
    () => filteredNvrs.flatMap((nvr) => nvr.cameras || []),
    [filteredNvrs]
  );

  // ── section header helper ────────────────────────────────────────────────────
  const SectionHeader = ({ label, expanded, onToggle, onPlaySection, showPlay = true }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "8px",
        py: "6px",
        backgroundColor: theme.palette.primary.background,
        cursor: "pointer",
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {expanded ? (
          <ArrowDropDownIcon sx={{ fontSize: 20, color: theme.palette.primary.dark }} />
        ) : (
          <ArrowRightIcon sx={{ fontSize: 20, color: theme.palette.primary.dark }} />
        )}
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.palette.primary.dark }}>
          {label}
        </Typography>
      </Box>
      {showPlay && (
        <IconButton
          size="small"
          disableRipple
          onClick={(e) => {
            e.stopPropagation();
            onPlaySection?.();
          }}
          sx={{
            p: "4px",
            borderRadius: "50%",
            transition: "all 0.2s",
            "&:hover": {
              backgroundColor: "rgba(23,38,100,0.08)",
            },
          }}
        >
          <PlayCircleIcon sx={{ fontSize: 22, color: theme.palette.primary.dark }} />
        </IconButton>
      )}
    </Box>
  );

  return (
    <>
      {/* Search ─────────────────────────────────────────────────────────────── */}
      <Box sx={{ px: "12px", pt: "16px", pb: "6px" }}>
        <SearchInput
          value={searchKeyword}
          onChange={setSearchKeyword}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              width: "100%",
            },
          }}
        />
      </Box>

      {/* Favorite ───────────────────────────────────────────────────────────── */}
      <Box sx={{ mt: "16px", mx: "12px" }}>
        <SectionHeader
          label={`Favorite (${favoriteDevices.length})`}
          expanded={favoritesExpanded}
          onToggle={() => setFavoritesExpanded((v) => !v)}
          onPlaySection={() => onPlayCameras?.(favoriteDevices)}
          showPlay={showSectionPlay}
        />
        <Collapse in={favoritesExpanded}>
          <Box sx={{ maxHeight: 180, overflowY: "auto" }}>
            {favoriteDevices.map((device) => (
              <FavoriteItem
                key={device.id}
                device={device}
                isFavorited={device.favorited === true}
                onPlay={onPlayCamera}
                onToggleFavorite={onToggleFavorite}
              />
            ))}
          </Box>
        </Collapse>
      </Box>

      {/* Device ─────────────────────────────────────────────────────────────── */}
      <Box sx={{ mt: "16px", mx: "12px" }}>
        <SectionHeader
          label={`Device (${filteredDevices.length})`}
          expanded={devicesExpanded}
          onToggle={() => setDevicesExpanded((v) => !v)}
          onPlaySection={() => onPlayCameras?.(filteredDevices)}
          showPlay={showSectionPlay}
        />
        <Collapse in={devicesExpanded}>
          <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
            {filteredDevices.map((device) => (
              <DeviceItem
                key={device.id}
                device={device}
                isFavorited={device.favorited === true}
                onToggleFavorite={onToggleFavorite}
                onSettings={onDeviceSettings}
                onPlay={onPlayCamera}
              />
            ))}
          </Box>
        </Collapse>
      </Box>

      {/* NVR ────────────────────────────────────────────────────────────────── */}
      <Box sx={{ mt: "16px", mx: "12px" }}>
        <SectionHeader
          label={`NVR (${filteredNvrs.length})`}
          expanded={nvrExpanded}
          onToggle={() => setNvrExpanded((v) => !v)}
          onPlaySection={() => onPlayCameras?.(nvrCamerasInOrder)}
          showPlay={showSectionPlay}
        />
        <Collapse in={nvrExpanded}>
          <Box sx={{ maxHeight: 220, overflowY: "auto", pt: "15px" }}>
            {filteredNvrs.map((nvr, index) => (
              <NvrItem
                key={nvr.id}
                nvr={nvr}
                onToggleFavorite={onToggleFavorite}
                onSettings={onDeviceSettings}
                onPlay={onPlayCamera}
                isFirst={index === 0}
                isLast={index === filteredNvrs.length - 1}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    </>
  );
};

export default CameraSearchPanel;
